import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useI18n } from "i18n";
import { Errors, trackError } from "modules/monitoring";
import useNotificationActions from "modules/notification/hooks/useNotificationActions";
import { useSelector } from "react-redux";
import { selectUserId } from "modules/auth/redux/selectors";
import { EMAIL_CUSTOMIZATION_QUERY_KEY } from "../../constants";
import { saveEmailCustomizationRequest } from "./request";
import { IEmailCustomization } from "../../types";

interface Options {
  onSuccess?: () => void;
}

export const useSaveEmailCustomization = (
  eventId: string | undefined,
  options: Options = {},
) => {
  const userId = useSelector(selectUserId);
  const { onSuccess } = options;
  const { addErrorNotification, addSuccessNotification } =
    useNotificationActions();
  const { t } = useI18n(["eventForm"]);
  const queryClient = useQueryClient();

  const {
    mutateAsync: saveCustomization,
    mutate: saveCustomizationSync,
    isPending: isLoading,
    status,
  } = useMutation({
    mutationFn: async (customizations: IEmailCustomization[]) => {
      if (!eventId) {
        throw new Error("No event ID provided");
      }

      return saveEmailCustomizationRequest(eventId, customizations);
    },
    onSuccess: (customizations) => {
      addSuccessNotification({
        message: t("eventForm:customize.emails.save.changes.success"),
        position: "tc",
        autoDismiss: 5,
      });
      queryClient.setQueryData(
        [EMAIL_CUSTOMIZATION_QUERY_KEY, eventId, userId],
        customizations,
      );

      if (onSuccess) {
        onSuccess();
      }
    },
    onError: (error: Error) => {
      addErrorNotification({
        message: t("eventForm:customize.emails.save.changes.error.message"),
        position: "tc",
      });
      trackError(error, {
        label: Errors.EMAIL_CUSTOMIZATION_SAVE,
      });
    },
  });

  return {
    isLoading,
    status,
    saveCustomization,
    saveCustomizationSync,
  };
};
